import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Photo, PhotosResponse } from '../../../shared/types/api';
import { togglePhotoLike } from '../../../shared/utils/api/api';
import { useRoom } from '../../../contexts/RoomContext';
import { useImageUpdate } from '../../../contexts/ImageUpdateContext';
import { getOrCreateGuestUserId } from '../../../shared/utils/user/guestUser';
import { getApiBaseUrl } from '../../../shared/config/urls';

/**
 * フィルター種別
 */
export type PhotoFilter = 'all' | 'recent' | 'popular';

/**
 * 写真ギャラリーの設定
 */
export interface PhotoGalleryConfig {
  autoReload?: {
    onVisibilityChange?: boolean;
    onImageUpdate?: boolean;
  };
  pageSize?: number;
}

export function usePhotoGalleryPaginated(config: PhotoGalleryConfig = {}) {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [filter, setFilter] = useState<PhotoFilter>('recent');
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const { roomId, isRoomSet, setRoomId } = useRoom();
  const { onImageUpdate } = useImageUpdate();
  const navigate = useNavigate();

  const pageSize = config.pageSize || 16;
  const API_BASE = getApiBaseUrl();

  const fetchPhotos = useCallback(async (page = 0, append = false): Promise<Photo[]> => {
    let currentRoomId = roomId;

    if (!isRoomSet || !roomId) {
      const savedRoomId = sessionStorage.getItem("roomId");
      if (savedRoomId) {
        setRoomId(savedRoomId);
        currentRoomId = savedRoomId;
      } else {
        throw new Error('ルームIDが設定されていません。ゲストログインページからアクセスしてください。');
      }
    }

    if (!currentRoomId) {
      throw new Error('ルームIDが設定されていません。ゲストログインページからアクセスしてください。');
    }

    const guestUserId = getOrCreateGuestUserId();

    // ルームIDをもとに、ローカルストレージから表示設定を取得
    const savedSettings = localStorage.getItem(`screen-settings-${currentRoomId}`);
    const onlyModerated = savedSettings ? JSON.parse(savedSettings).onlyModerated : true;

    const response = await fetch(
      `${API_BASE}/api/picture/fetchImages?roomId=${currentRoomId}&page=${page}&size=${pageSize}&guestUserId=${guestUserId}&sort=${filter}&onlyModerated=${onlyModerated}`
    );

    if (!response.ok) {
      throw new Error('写真の取得に失敗しました');
    }

    const data: PhotosResponse = await response.json();

    if ('error' in data) {
      throw new Error(data.error as string);
    }

    const photosWithUrls = data.images.map(photo => {
      return {
        ...photo,
        imageUrl: `${API_BASE}${photo.imageUrl}`,
        thumbnailUrl: `${API_BASE}${photo.imageUrl}`,
        isLiked: photo.isLiked ?? false,
      };
    });

    if (append) {
      setPhotos(prev => [...prev, ...photosWithUrls]);
    } else {
      setPhotos(photosWithUrls);
    }

    setTotalPages(data.totalPages);
    setCurrentPage(data.currentPage);
    setTotalElements(data.totalElements);
    setHasNextPage(data.hasNext);

    return photosWithUrls;
  }, [roomId, isRoomSet, setRoomId, pageSize, API_BASE, filter]); // ✅ filterを追加

  const loadPhotos = useCallback(async (page = 0, append = false) => {
    const loadingState = append ? setIsLoadingMore : setLoading;

    try {
      loadingState(true);
      setError(null);
      await fetchPhotos(page, append);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '写真の取得に失敗しました';
      setError(errorMessage);
      console.error('写真の取得に失敗しました:', error);

      if (errorMessage.includes('ルームID')) {
        setTimeout(() => {
          navigate('/');
        }, 3000);
      }
    } finally {
      loadingState(false);
    }
  }, [fetchPhotos, navigate]);

  const loadMore = useCallback(() => {
    if (hasNextPage && !loading && !isLoadingMore) {
      loadPhotos(currentPage + 1, true);
    }
  }, [currentPage, hasNextPage, loading, isLoadingMore, loadPhotos]);

  useEffect(() => {
    setCurrentPage(0);
    loadPhotos(0, false);
  }, [filter, roomId, isRoomSet, loadPhotos]);

  useEffect(() => {
    if (!config.autoReload?.onImageUpdate) return;

    const unsubscribe = onImageUpdate(() => {
      setCurrentPage(0);
      loadPhotos(0, false);
      setSelectedPhoto(null);
    });

    return unsubscribe;
  }, [onImageUpdate, loadPhotos, config.autoReload?.onImageUpdate]);

  useEffect(() => {
    if (!config.autoReload?.onVisibilityChange) return;

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        setCurrentPage(0);
        loadPhotos(0, false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadPhotos, config.autoReload?.onVisibilityChange]);

  const handleLike = useCallback(async (photoId: string) => {
    console.log('[usePhotoGalleryPaginated] handleLike called for photo:', photoId);
    
    const currentPhoto = photos.find(p => p.id === photoId);
    if (!currentPhoto) {
      console.error('[usePhotoGalleryPaginated] Photo not found:', photoId);
      return;
    }

    console.log('[usePhotoGalleryPaginated] Current photo state:', {
      id: currentPhoto.id,
      likes: currentPhoto.likes,
      isLiked: currentPhoto.isLiked
    });

    const updatePhotoLikes = (photo: Photo) => {
      if (photo.id === photoId) {
        const newIsLiked = !photo.isLiked;
        const newLikes = newIsLiked ? photo.likes + 1 : Math.max(0, photo.likes - 1);
        console.log('[usePhotoGalleryPaginated] Optimistic update:', {
          oldLikes: photo.likes,
          newLikes,
          oldIsLiked: photo.isLiked,
          newIsLiked
        });
        return { ...photo, likes: newLikes, isLiked: newIsLiked };
      }
      return photo;
    };

    const originalPhotos = photos;
    const originalSelectedPhoto = selectedPhoto;

    // 楽観的更新
    console.log('[usePhotoGalleryPaginated] Applying optimistic update');
    setPhotos(prev => prev.map(updatePhotoLikes));
    if (selectedPhoto && selectedPhoto.id === photoId) {
      setSelectedPhoto(prev => prev ? updatePhotoLikes(prev) : null);
    }

    try {
      console.log('[usePhotoGalleryPaginated] Calling API togglePhotoLike');
      const response = await togglePhotoLike(photoId);
      console.log('[usePhotoGalleryPaginated] API response:', response);

      if (response.success) {
        console.log('[usePhotoGalleryPaginated] API success, updating with server data');
        setPhotos(prev => prev.map(photo => {
          if (photo.id === photoId) {
            return {
              ...photo,
              likes: response.likesCount,
              isLiked: response.isLikedByCurrentUser
            };
          }
          return photo;
        }));

        if (selectedPhoto && selectedPhoto.id === photoId) {
          setSelectedPhoto(prev => prev ? {
            ...prev,
            likes: response.likesCount,
            isLiked: response.isLikedByCurrentUser
          } : null);
        }

      } else {
        console.error('[usePhotoGalleryPaginated] API returned failure:', response.message);
        throw new Error(response.message);
      }
    } catch (error) {
      console.error('[usePhotoGalleryPaginated] API call failed, reverting optimistic update:', error);
      setPhotos(originalPhotos);
      setSelectedPhoto(originalSelectedPhoto);

      console.error('❌ いいねの更新に失敗しました:', error);
      alert('いいねの更新に失敗しました。しばらく時間をおいて再度お試しください。');
    }
  }, [photos, selectedPhoto]);

  const changeFilter = useCallback((newFilter: PhotoFilter) => {
    setFilter(newFilter);
  }, []);

  const selectPhoto = useCallback((photo: Photo) => {
    setSelectedPhoto(photo);
  }, []);

  const closeModal = useCallback(() => {
    setSelectedPhoto(null);
  }, []);

  const manualRefresh = useCallback(() => {
    setCurrentPage(0);
    loadPhotos(0, false);
    setSelectedPhoto(null);
  }, [loadPhotos]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    photos,
    filter,
    selectedPhoto,
    loading,
    error,
    isLoadingMore,
    currentPage,
    totalPages,
    totalElements,
    hasNextPage,
    loadMore,
    manualRefresh,
    handleLike,
    changeFilter,
    selectPhoto,
    closeModal,
    clearError,
    roomId,
    isRoomSet,
  };
}
